import { useEffect, useState } from "react";
import { Plus, KeyRound, Copy, Ban, Trash2, ShieldCheck, Bot, UserCog } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full border border-tertiary bg-white px-3 py-2 text-sm outline-none focus:border-champagne focus:ring-1 focus:ring-champagne";
const ACTIONS = [
  { key: "create", label: "Saisie", hint: "Créer des brouillons" },
  { key: "update", label: "Édition", hint: "Modifier le contenu (sans publier)" },
  { key: "delete", label: "Suppression", hint: "Supprimer des fiches" },
];
const MODULES = [
  { key: "opportunities", label: "Opportunités" },
  { key: "projects", label: "Projets" },
  { key: "requests", label: "Demandes" },
];
const API_BASE = process.env.REACT_APP_SITE_URL || process.env.REACT_APP_BACKEND_URL;

export default function AccessAdmin() {
  const [editors, setEditors] = useState([]);
  const [keys, setKeys] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [keyForm, setKeyForm] = useState({ name: "", scopes: MODULES.map((m) => m.key) });
  const [newKey, setNewKey] = useState(null);

  const load = () => {
    api.get("/admin/access/editors").then(({ data }) => setEditors(data)).catch(() => {});
    api.get("/admin/access/api-keys").then(({ data }) => setKeys(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const addEditor = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/access/editors", form);
      toast.success("Éditeur créé — accès limité à la saisie");
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const togglePerm = async (ed, action) => {
    const permissions = { ...ed.permissions, [action]: !ed.permissions[action] };
    await api.put(`/admin/access/editors/${ed.id}`, { permissions });
    load();
  };
  const toggleActive = async (ed) => { await api.put(`/admin/access/editors/${ed.id}`, { active: !ed.active }); toast.success(ed.active ? "Accès suspendu" : "Accès rétabli"); load(); };
  const removeEditor = async (ed) => { if (!window.confirm(`Supprimer définitivement l'accès de ${ed.email} ?`)) return; await api.delete(`/admin/access/editors/${ed.id}`); load(); };

  const createKey = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/access/api-keys", keyForm);
      setNewKey(data.key);
      setKeyForm({ name: "", scopes: MODULES.map((m) => m.key) });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const revokeKey = async (k) => { if (!window.confirm(`Révoquer la clé « ${k.name} » ? L'agent perdra immédiatement l'accès.`)) return; await api.post(`/admin/access/api-keys/${k.key_id}/revoke`); toast.success("Clé révoquée"); load(); };
  const deleteKey = async (k) => { if (!window.confirm("Supprimer définitivement cette clé ?")) return; await api.delete(`/admin/access/api-keys/${k.key_id}`); load(); };
  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Copié"); };

  return (
    <div className="max-w-5xl" data-testid="access-admin">
      <h1 className="mb-1 font-serif text-3xl text-anthracite">Gestion des accès</h1>
      <p className="mb-8 text-sm text-anthracite/50">Accès limités à la saisie sur Opportunités, Projets et Demandes. Seul l'admin principal publie, qualifie, modifie et supprime.</p>

      {/* EDITORS */}
      <section className="mb-12" data-testid="access-editors">
        <div className="mb-4 flex items-center gap-2"><UserCog className="h-5 w-5 text-champagne" /><h2 className="font-serif text-2xl text-anthracite">Éditeurs (humains)</h2></div>
        <form onSubmit={addEditor} data-testid="editor-form" className="mb-5 grid gap-3 border border-tertiary bg-white p-5 sm:grid-cols-4">
          <input placeholder="Nom" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required data-testid="editor-name" className={inputCls} />
          <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required data-testid="editor-email" className={inputCls} />
          <input placeholder="Mot de passe (8+)" type="password" minLength={8} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} required data-testid="editor-password" className={inputCls} />
          <button type="submit" data-testid="editor-add" className="inline-flex items-center justify-center gap-2 bg-anthracite px-4 py-2 text-sm font-medium text-ivory hover:bg-black"><Plus className="h-4 w-4" /> Créer un éditeur</button>
        </form>
        <div className="border border-tertiary bg-white">
          {editors.length === 0 ? <p className="px-5 py-10 text-center text-sm text-anthracite/40" data-testid="editors-empty">Aucun éditeur.</p> : editors.map((ed) => (
            <div key={ed.id} data-testid={`editor-row-${ed.id}`} className={`flex flex-wrap items-center justify-between gap-4 border-b border-tertiary px-5 py-4 last:border-0 ${ed.active ? "" : "opacity-60"}`}>
              <div className="min-w-[200px]">
                <p className="text-sm font-medium text-anthracite">{ed.name} {!ed.active && <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-[10px] uppercase tracking-wider text-red-600">suspendu</span>}</p>
                <p className="text-xs text-anthracite/50">{ed.email}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {ACTIONS.map((a) => (
                  <button key={a.key} type="button" title={a.hint} onClick={() => togglePerm(ed, a.key)} data-testid={`editor-perm-${a.key}-${ed.id}`}
                    className={`rounded-full border px-3 py-1 text-xs transition-colors duration-300 ${ed.permissions[a.key] ? "border-anthracite bg-anthracite text-ivory" : "border-tertiary text-anthracite/50 hover:border-anthracite"}`}>
                    {a.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-1.5">
                <button onClick={() => toggleActive(ed)} data-testid={`editor-toggle-${ed.id}`} title={ed.active ? "Suspendre" : "Rétablir"} className="rounded p-1.5 text-anthracite/60 hover:text-champagne"><Ban className="h-4 w-4" /></button>
                <button onClick={() => removeEditor(ed)} data-testid={`editor-delete-${ed.id}`} className="rounded p-1.5 text-anthracite/60 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* API KEYS */}
      <section data-testid="access-api-keys">
        <div className="mb-4 flex items-center gap-2"><Bot className="h-5 w-5 text-champagne" /><h2 className="font-serif text-2xl text-anthracite">Agents IA (clés API)</h2></div>
        <p className="mb-4 text-xs text-anthracite/50">Accès API uniquement, saisie seule (brouillons). Impossible de publier, modifier, supprimer ou lire d'autres modules.</p>
        <form onSubmit={createKey} data-testid="apikey-form" className="mb-5 flex flex-wrap items-end gap-3 border border-tertiary bg-white p-5">
          <div className="min-w-[220px] flex-1"><label className="mb-1 block text-xs uppercase tracking-[0.12em] text-anthracite/50">Nom de l'agent</label><input value={keyForm.name} onChange={(e) => setKeyForm((f) => ({ ...f, name: e.target.value }))} required data-testid="apikey-name" className={inputCls} /></div>
          <div className="flex flex-wrap gap-2">
            {MODULES.map((m) => (
              <label key={m.key} className="flex items-center gap-1.5 text-xs text-anthracite/70"><input type="checkbox" checked={keyForm.scopes.includes(m.key)} onChange={() => setKeyForm((f) => ({ ...f, scopes: f.scopes.includes(m.key) ? f.scopes.filter((x) => x !== m.key) : [...f.scopes, m.key] }))} data-testid={`apikey-scope-${m.key}`} />{m.label}</label>
            ))}
          </div>
          <button type="submit" data-testid="apikey-create" className="inline-flex items-center gap-2 bg-anthracite px-4 py-2 text-sm font-medium text-ivory hover:bg-black"><KeyRound className="h-4 w-4" /> Générer une clé</button>
        </form>

        {newKey && (
          <div className="mb-5 border border-champagne bg-champagne/10 p-5" data-testid="apikey-reveal">
            <p className="mb-2 flex items-center gap-2 text-sm font-medium text-anthracite"><ShieldCheck className="h-4 w-4 text-champagne" /> Copiez cette clé maintenant — elle ne sera plus jamais affichée.</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 overflow-x-auto bg-white px-3 py-2 text-xs" data-testid="apikey-value">{newKey}</code>
              <button onClick={() => copy(newKey)} className="inline-flex items-center gap-1.5 border border-anthracite px-3 py-2 text-xs hover:bg-anthracite hover:text-ivory"><Copy className="h-3.5 w-3.5" /> Copier</button>
              <button onClick={() => setNewKey(null)} className="px-3 py-2 text-xs text-anthracite/60 hover:text-anthracite">Fermer</button>
            </div>
            <p className="mt-3 text-xs text-anthracite/60">Utilisation : en-tête <code>X-API-Key</code> sur <code>POST {API_BASE}/api/admin/opportunities</code>, <code>/api/admin/projects</code>, <code>/api/admin/requests</code>. Les fiches créées sont enregistrées en brouillon.</p>
          </div>
        )}

        <div className="border border-tertiary bg-white">
          {keys.length === 0 ? <p className="px-5 py-10 text-center text-sm text-anthracite/40" data-testid="apikeys-empty">Aucune clé API.</p> : keys.map((k) => (
            <div key={k.key_id} data-testid={`apikey-row-${k.key_id}`} className={`flex flex-wrap items-center justify-between gap-4 border-b border-tertiary px-5 py-4 last:border-0 ${k.revoked_at ? "opacity-60" : ""}`}>
              <div>
                <p className="text-sm font-medium text-anthracite">{k.name} <span className="ml-2 font-mono text-[11px] text-anthracite/40">rsti_agent_{k.key_id}_…</span> {k.revoked_at && <span className="ml-2 rounded-full bg-red-50 px-2 py-0.5 text-[10px] uppercase tracking-wider text-red-600">révoquée</span>}</p>
                <p className="text-xs text-anthracite/50">{k.scopes.map((s) => MODULES.find((m) => m.key === s)?.label).join(" · ")} · créée le {new Date(k.created_at).toLocaleDateString("fr")}{k.last_used_at ? ` · dernier usage ${new Date(k.last_used_at).toLocaleString("fr")}` : " · jamais utilisée"}</p>
              </div>
              <div className="flex items-center gap-1.5">
                {!k.revoked_at && <button onClick={() => revokeKey(k)} data-testid={`apikey-revoke-${k.key_id}`} className="inline-flex items-center gap-1.5 border border-tertiary px-3 py-1.5 text-xs hover:border-red-600 hover:text-red-600"><Ban className="h-3.5 w-3.5" /> Révoquer</button>}
                <button onClick={() => deleteKey(k)} data-testid={`apikey-delete-${k.key_id}`} className="rounded p-1.5 text-anthracite/60 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
